import React, { FC, ReactElement, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Session, updateHeaderConfig } from 'src/front/api/apiInstance';

import Application from 'src/front/components/Application';

import { StepperRefProps } from 'src/front/components/StepperView';

import ClientInfoProvider, { useClearDispatch } from 'src/front/contexts/ClientInfoContext';

import { SubMenuIds } from 'src/front/routes/paths';
import ClientInfoDocumentUpload from './ClientInfoDocumentUpload';
import ClientInfoSearch from './ClientInfoSearch';
import DocumentTaxInformation from './DocumentTaxInformation';
import IndividualInformation from './IndividualInformation';

const ClientInfo: FC = () => {
  const { t } = useTranslation();
  const stepperRef = useRef<StepperRefProps>(null);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const clearOldData = useClearDispatch();
  const goToNextStep = (): void => {
    stepperRef.current?.goToNextStep();
  };

  const onRepeat = async (): Promise<void> => {
    const { data, headers } = await Session.clearSessionData({
      menuID: SubMenuIds.CLIENT_INFO,
    });

    if (data.result === 'OK') {
      clearOldData();
      updateHeaderConfig(data.sessionID, headers.authorization);
      stepperRef.current?.goToFirstStep();
    }
  };

  const clearCustomerSearch = async (): Promise<void> => {
    const { data, headers } = await Session.clearSessionData({
      menuID: SubMenuIds.CLIENT_INFO,
    });

    if (data.result === 'OK') {
      clearOldData();
      updateHeaderConfig(data.sessionID, headers.authorization);
    }
  };

  const steps = [
    {
      component: () => (
        <ClientInfoSearch clearCustomerSearch={clearCustomerSearch} goToNextStep={goToNextStep} />
      ),
    },
    {
      component: () => <IndividualInformation goToNextStep={goToNextStep} />,
    },
    {
      component: () => <DocumentTaxInformation goToNextStep={goToNextStep} />,
    },
    {
      component: () => (
        <ClientInfoDocumentUpload onRepeat={onRepeat} setIsCompleted={setIsCompleted} />
      ),
    },
  ];

  const stepsVertical = useMemo(() => {
    return [
      {
        index: 1,
        text: t('changeFundClientInfo_stepper_clientSearch_text'),
      },
      {
        index: 2,
        text: t('changeFundClientInfo_stepper_changeClientInfo_text'),
        subSteps: [
          {
            index: 1,
            text: t('changeFundClientInfo_stepper_changeClientInfoStepSub1_text'),
          },
          {
            index: 2,
            text: t('changeFundClientInfo_stepper_changeClientInfoStepSub2_text'),
          },
          {
            index: 3,
            text: t('changeFundClientInfo_stepper_changeClientInfoStepSub3_text'),
          },
        ],
      },
    ];
  }, [t]);

  return (
    <Application
      isCompleted={isCompleted}
      stepperRef={stepperRef}
      steps={steps}
      stepsVertical={stepsVertical}
    />
  );
};

export default (): ReactElement => (
  <ClientInfoProvider>
    <ClientInfo />
  </ClientInfoProvider>
);
